import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import {
  ListPanelEntityModel,
  QuoteModel,
  SolutionsModel,
  VideoModel,
} from "../ListPanel/ListPanelModel";
import { ProductModel } from "../ShowcasePanel/ShowcasePanelModel";
import { PageModel } from "../../core/Models/PageModel";
import ListPanelUtils from "../ListPanel/ListPanelUtils";
import { PeopleItemModel } from "../ListPanel/PeopleItem/PeopleItemModel";
import PeopleItem from "../ListPanel/PeopleItem/PeopleItem";
import ListPanelItem from "../ListPanel/ListPanelItem/ListPanelItem";
import { FilterListPanelModel } from "./FilterListPanelModel";
import SupportUtils from "../../support/SupportUtils";

import "./FilterListPanel.css";

const FilterListPanel: React.FC<{ blok: FilterListPanelModel }> = ({
  blok,
}) => {
  const [data, setData] = useState<ListPanelEntityModel[]>([]);
  const navigate = useNavigate();

  const contentRef = useRef<HTMLDivElement | null>(null);
  const listPanelRef = useRef<HTMLDivElement | null>(null);
  const [showPopup, setShowPopup] = useState<string | undefined>(undefined);

  useEffect(() => {
    SupportUtils.getCachedOrStoryblokSearch(
      "",
      undefined,
      null,
      blok.category ? blok.category : null,
      null,
      blok.type
    )
      .then((response: any) => {
        const data = response.data?.story || response.data?.stories;
        const sortData = data?.sort((a: any, b: any) => {
          return (
            new Date(b.published_at).getTime() -
            new Date(a.published_at).getTime()
          );
        });
        setData(sortData);
      })
      .catch((error) => {
        if (error.status === 404) {
          navigate("/404");
        } else if (error.status === 500) {
          navigate("/500");
        }
      });
  }, [navigate, blok.category, blok.type]);

  const handleClick = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel,
    uuid: string
  ) => {
    if (ListPanelUtils.isVideoEntity(entity)) {
      setShowPopup(uuid);
    }
  };

  useLayoutEffect(() => {
    if (blok.itemsClass === "horizontal-scroll" && window.innerWidth > 768) {
      gsap.registerPlugin(ScrollTrigger);
      const panel = contentRef.current;
      const wrapper = listPanelRef.current;

      if (!panel || !wrapper) return;

      const scrollWidth = panel.scrollWidth;
      const navHeight =
        document.querySelector(".notification-strip-nav")?.clientHeight ?? 0;

      const animation = gsap.to(panel, {
        x: () => `-${panel.scrollWidth - panel.clientWidth}px`,
        ease: "none",
        scrollTrigger: {
          trigger: wrapper,
          start: `-${navHeight} top`,
          end: () => `+=${scrollWidth}`,
          scrub: true,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      return () => {
        if (panel) {
          gsap.killTweensOf(panel);
        }
        animation.kill();
        ScrollTrigger.getAll().forEach((trigger) => trigger?.kill());
      };
    }
  }, [blok.itemsClass]);

  return (
    <div
      {...storyblokEditable(blok)}
      className="list-panel"
      style={{ backgroundColor: blok.panelColour }}
    >
      <div className="list-panel-ref" ref={listPanelRef}>
        <div className="list-panel-container" style={{ color: blok.textColor }}>
          <div className="title">
            <span>{blok.title}</span>
          </div>
          <div
            className={`list-panel-content ${blok.itemsClass}`}
            ref={contentRef}
          >
            {data
              ? data.map((entity) => {
                  if (!entity || !entity.content) {
                    return null;
                  }
                  if (ListPanelUtils.isPeopleEntity(entity.content)) {
                    return (
                      <PeopleItem
                        key={`list-panell-people-item-${entity.uuid}`}
                        blok={entity.content as PeopleItemModel}
                      ></PeopleItem>
                    );
                  } else {
                    return (
                      <ListPanelItem
                        key={`list-panel-ite-${
                          typeof entity.uuid === "string"
                            ? entity.uuid
                            : (entity.uuid as any)?.uuid
                        }`}
                        entity={entity}
                        containerColor={blok.containerColor}
                        fontFamily={blok.style}
                        showPopup={showPopup}
                        handleClick={() =>
                          handleClick(entity.content, entity.uuid)
                        }
                        onVideoButtonClick={() => setShowPopup(undefined)}
                      ></ListPanelItem>
                    );
                  }
                })
              : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterListPanel;
